// Правка заказчика. До 15.09.2026 исправить в нём нельзя было ни одной буквы: и на сервере, и
// на экране у заказчика был только путь «завести».
//
// Это не редкий случай, а первый день работы. Заказчика заводят с чужих слов: ИНН переписывают
// из письма, телефон записывают со слуха, название сокращают по-своему. Единственным выходом
// оставалось завести заказчика ЗАНОВО — то есть оставить в справочнике двойника, к которому
// привязана половина договоров и объектов, и дальше объяснять коллегам, какой из двух живой.
//
// Заказчик не удаляется, а перестаёт быть действующим: на нём висят договоры, объекты и
// ведомости, и удаление порвало бы их. Флажок стоит прямо в форме, а не отдельной модалкой,
// как у объекта: снятие действия у заказчика обратимо тем же флажком, и подтверждать нечего.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiBriefcaseLine, RiErrorWarningLine } from '@remixicon/react';

import type { CustomerPatch } from '@/shared/api/endpoints/org';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { CheckboxField } from '@/shared/ui/checkbox-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { EmptyState, Skeleton } from '@/shared/ui/screen-state';
import { TextField } from '@/shared/ui/text-field';
import { useCustomersQuery } from '@/shared/org/queries';

import { usePatchCustomerMutation } from '../queries';

/** Правка заказчика — то же право, что и заведение (server/app/api/v1/dictionaries.py). */
const EDIT_PERMISSION = 'core.dictionary.edit';

function илиПусто(значение: string): string | null {
  const очищенное = значение.trim();
  return очищенное === '' ? null : очищенное;
}

export function EditCustomerPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const права = useAuthStore((state) => state.permissions);
  const можно = can(права, EDIT_PERMISSION);
  // Список целиком, а не отобранный поиском колонки: панель адресуема ссылкой (правило 4), и
  // открыть её могут не с того экрана, где заказчика нашли поиском.
  const заказчики = useCustomersQuery({});
  const правка = usePatchCustomerMutation(id);

  const [черновик, setЧерновик] = useState<{
    название: string;
    отрасль: string;
    регион: string;
    инн: string;
    контакт: string;
    телефон: string;
    действует: boolean;
  } | null>(null);
  const [ошибки, setОшибки] = useState<{ название?: string; инн?: string }>({});
  const [заслон, setЗаслон] = useState<string | null>(null);

  if (заказчики.data === undefined) {
    return (
      <div className='flex flex-col gap-3'>
        <Skeleton className='h-10 w-full' />
        <Skeleton className='h-10 w-full' />
        <Skeleton className='h-10 w-2/3' />
      </div>
    );
  }

  const заказчик = заказчики.data.items.find((строка) => строка.id === id);
  if (заказчик === undefined) {
    return (
      <EmptyState
        icon={RiBriefcaseLine}
        title='Этого заказчика больше нет в справочнике'
        description='Ссылка ведёт на заказчика, которого не видно в списке: либо её прислали до того, как справочник поменяли, либо у вас нет доступа к справочникам. Вернитесь к списку заказчиков.'
      />
    );
  }

  const поля = черновик ?? {
    название: заказчик.name,
    отрасль: заказчик.industry ?? '',
    регион: заказчик.region ?? '',
    инн: заказчик.inn ?? '',
    контакт: заказчик.contact_person ?? '',
    телефон: заказчик.contact_phone ?? '',
    действует: заказчик.is_active,
  };

  function поставить<К extends keyof typeof поля>(ключ: К, значение: (typeof поля)[К]) {
    setЧерновик({ ...поля, [ключ]: значение });
  }

  function сохранить(событие: FormEvent<HTMLFormElement>) {
    событие.preventDefault();
    if (!можно) {
      setЗаслон(
        'Править заказчиков может тот, у кого есть право на справочники. Попросите администратора выдать его — набранное никуда не денется.',
      );
      return;
    }
    if (правка.isPending) {
      setЗаслон('Заказчик уже сохраняется, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }

    const найдено: { название?: string; инн?: string } = {};
    if (поля.название.trim() === '') {
      найдено.название = 'Название стереть нельзя: по нему заказчика находят в реестре и отчётах.';
    }
    // Проверка длины, а не «похоже на ИНН»: ровно ради этой ошибки экран и заведён — цифру при
    // переписывании из письма теряют чаще, чем набирают лишнюю.
    const цифрыИНН = поля.инн.replace(/\D/g, '');
    if (поля.инн.trim() !== '' && цифрыИНН.length !== 10 && цифрыИНН.length !== 12) {
      найдено.инн = 'В ИНН 10 цифр у организации и 12 у предпринимателя. Проверьте, все ли на месте.';
    }
    if (Object.keys(найдено).length > 0) {
      setОшибки(найдено);
      return;
    }

    setОшибки({});
    setЗаслон(null);
    // Пустая строка уходит как «убрать значение», а не как «не трогать»: контактное лицо
    // уволилось, и старое имя в карточке врёт хуже, чем его отсутствие.
    const тело: CustomerPatch = {
      name: поля.название.trim(),
      industry: илиПусто(поля.отрасль),
      region: илиПусто(поля.регион),
      inn: илиПусто(поля.инн),
      contact_person: илиПусто(поля.контакт),
      contact_phone: илиПусто(поля.телефон),
      is_active: поля.действует,
    };
    правка.mutate(тело, { onSuccess: onClose });
  }

  return (
    <form onSubmit={сохранить} noValidate className='flex flex-col gap-3'>
      {!можно && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Права на правку справочников у вас нет: форму можно заполнить, но сохранить заказчика
            не выйдет. Право выдаёт администратор.
          </span>
        </Alert.Root>
      )}

      <TextField
        id='edit-customer-name'
        label='Название'
        required
        icon={RiBriefcaseLine}
        value={поля.название}
        onChange={(значение) => поставить('название', значение)}
        error={ошибки.название}
      />

      <div className='grid grid-cols-2 gap-2'>
        <TextField
          id='edit-customer-industry'
          label='Отрасль'
          value={поля.отрасль}
          onChange={(значение) => поставить('отрасль', значение)}
          placeholder='Нефтегаз'
        />
        <TextField
          id='edit-customer-region'
          label='Регион'
          value={поля.регион}
          onChange={(значение) => поставить('регион', значение)}
          placeholder='ХМАО'
        />
      </div>

      <TextField
        id='edit-customer-inn'
        label='ИНН'
        value={поля.инн}
        onChange={(значение) => поставить('инн', значение)}
        inputMode='numeric'
        maxLength={12}
        error={ошибки.инн}
        hint='10 цифр у организации, 12 у предпринимателя.'
      />

      <TextField
        id='edit-customer-contact'
        label='Контактное лицо'
        value={поля.контакт}
        onChange={(значение) => поставить('контакт', значение)}
        placeholder='Фамилия и инициалы'
      />

      <TextField
        id='edit-customer-phone'
        label='Телефон'
        value={поля.телефон}
        onChange={(значение) => поставить('телефон', значение)}
        inputMode='tel'
        placeholder='+7 982 726-68-53'
        hint='По нему звонят, когда на объекте что-то случилось.'
      />

      <CheckboxField
        id='edit-customer-active'
        label='Заказчик действует'
        checked={поля.действует}
        onCheckedChange={(значение) => поставить('действует', значение)}
        hint='Снимите отметку, если работу с ним закончили: он пропадёт из выбора при заведении объектов, а его договоры, объекты и ведомости останутся на месте.'
      />

      {правка.isError && <ErrorAlert error={правка.error} />}

      {заслон !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {заслон}
        </Hint.Root>
      )}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {правка.isPending ? 'Сохраняем…' : 'Сохранить'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
